import React, { useState, useEffect, useCallback } from 'react';
import { MedicationScheduleEntry, MyMedication } from '../types';
import { myMedicationService } from '../lib/myMedicationService';
import { EmptyState } from './common/EmptyState';
import { SkeletonList } from './common/Skeleton';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../utils/errorMessages';

interface MedicationTrackerProps {
  myMedications: MyMedication[];
  onAddMedication?: () => void;
}

export const MedicationTracker: React.FC<MedicationTrackerProps> = ({ myMedications, onAddMedication }) => {
  const [todayEntries, setTodayEntries] = useState<MedicationScheduleEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTodayEntries = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const entries = await myMedicationService.getScheduleEntries(todayStr, todayStr, 'today');
      
      console.log('📋 Załadowane wpisy z harmonogramu:', entries);
      console.log('💊 Dostępne leki (myMedications):', myMedications);
      
      setTodayEntries(entries);
    } catch (error) {
      console.error('Błąd ładowania dzisiejszych leków:', error);
      setTodayEntries([]);
    } finally {
      setLoading(false);
    }
  }, [myMedications]);

  useEffect(() => {
    loadTodayEntries();
  }, [loadTodayEntries]);

  const toggleMedication = async (entryId: string, currentlyTaken: boolean) => {
    try {
      await myMedicationService.markAsTaken(entryId, !currentlyTaken);
      await loadTodayEntries();
      toast.success(!currentlyTaken ? 'Lek oznaczony jako przyjęty' : 'Oznaczenie cofnięte', { duration: 2000 });
    } catch (error) {
      console.error('Błąd aktualizacji wpisu:', error);
      toast.error(getErrorMessage(error));
    }
  };

  // Grupuj leki po godzinach
  const groupedByTime = todayEntries.reduce((acc, entry) => {
    const time = entry.time;
    if (!acc[time]) {
      acc[time] = [];
    }
    acc[time].push(entry);
    return acc;
  }, {} as Record<string, MedicationScheduleEntry[]>);

  // Sortuj godziny
  const sortedTimes = Object.keys(groupedByTime).sort((a, b) => a.localeCompare(b));

  // Statystyki
  const totalCount = todayEntries.length;
  const takenCount = todayEntries.filter(e => e.taken).length;
  const remainingCount = totalCount - takenCount;
  const progressPercentage = totalCount > 0 ? (takenCount / totalCount) * 100 : 0;

  // Pobierz nazwę leku na podstawie ID
  const getMedicationName = (entry: MedicationScheduleEntry) => {
    console.log('🔍 Szukam leku dla ID:', entry.myMedicationId);
    console.log('📋 Dostępne ID:', myMedications.map(m => ({ id: m.id, name: m.name })));
    
    const medication = myMedications.find(m => m.id === entry.myMedicationId);
    
    if (medication) {
      console.log('✅ Znaleziono:', medication.name);
      return medication.name;
    }
    
    console.warn('❌ Nie znaleziono leku dla ID:', entry.myMedicationId);
    return `Nieznany lek (ID: ${entry.myMedicationId})`;
  };

  if (loading && todayEntries.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl mb-1 font-bold text-gray-900">Moje leki</h1>
        <SkeletonList count={4} />
      </div>
    );
  }

  if (myMedications.length === 0) {
    return (
      <EmptyState
        icon={
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.5 20.5L3.5 13.5a5 5 0 117.07-7.07l9.9 9.9a5 5 0 11-7.07 7.07L10.5 20.5z"/>
            <path d="M8.5 8.5l7 7"/>
          </svg>
        }
        title="Brak leków do odhaczenia"
        description="Dodaj leki i ustaw harmonogram, aby móc odhaczać ich przyjmowanie."
        actionLabel="Dodaj pierwszy lek"
        onAction={onAddMedication}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <h1 className="text-2xl mb-1 font-bold text-gray-900">Moje leki</h1>

      {/* Statystyki */}
      <div className="mb-6 bg-white rounded-3xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-semibold text-gray-900">{totalCount}</div>
            <div className="text-sm text-gray-600">Zaplanowane</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-[#00A86B]">{takenCount}</div>
            <div className="text-sm text-gray-600">Przyjęte</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-[#F97316]">{remainingCount}</div>
            <div className="text-sm text-gray-600">Pozostało</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${progressPercentage}%`,
              background: 'linear-gradient(to right, #00A86B, #00C781)'
            }}
          />
        </div>
      </div>

      {/* Przycisk Dodaj lek */}
      <button
        onClick={() => {}}
        className="mb-6 w-full py-3 px-4 rounded-3xl shadow-sm text-white font-medium transition-all duration-200 hover:shadow-md flex items-center justify-center gap-2"
        style={{
          background: 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
        }}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Dodaj lek
      </button>

      {/* Timeline */}
      {loading && todayEntries.length === 0 ? (
        <div className="text-center py-12 text-gray-600">
          Ładowanie...
        </div>
      ) : sortedTimes.length === 0 ? (
        <div className="p-10 text-center bg-gray-50 rounded-3xl shadow-sm border border-gray-200">
          <p className="text-lg mb-2 text-gray-600">
            Brak zaplanowanych leków na dzisiaj
          </p>
          <p className="text-sm text-gray-500">
            Dodaj leki, aby zobaczyć harmonogram
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Pionowa linia timeline */}
          <div
            className="absolute left-5 top-0 bottom-0"
            style={{
              width: '2px',
              backgroundColor: '#F0F0F0'
            }}
          />

          {/* Grupy godzin */}
          <div className="space-y-6">
            {sortedTimes.map((time, timeIndex) => {
              const entries = groupedByTime[time];
              const isLast = timeIndex === sortedTimes.length - 1;

              return (
                <div key={time} className="relative flex items-start gap-4">
                  {/* Marker godziny */}
                  <div
                    className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 flex items-center justify-center"
                    style={{
                      borderColor: '#E0E7FF',
                      marginLeft: '5px'
                    }}
                  >
                    {/* Ikona Clock */}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: '#6366F1' }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>

                  {/* Zawartość grupy */}
                  <div className="flex-1 pb-6" style={{ paddingBottom: isLast ? '0' : '1.5rem' }}>
                    {/* Godzina */}
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">{time}</h3>
                    </div>

                    {/* Lista leków */}
                    <div className="space-y-2">
                      {entries.map((entry) => {
                        const medication = myMedications.find(m => m.id === entry.myMedicationId);
                        const medicationName = getMedicationName(entry);
                        const isTaken = entry.taken;
                        const takingLabel = medication?.takingInstructions === 'after_meal'
                          ? 'Po posiłku'
                          : medication?.takingInstructions === 'with_meal' || medication?.withMeal
                            ? 'W trakcie posiłku'
                            : medication?.takingInstructions === 'before_meal'
                              ? 'Przed posiłkiem'
                              : medication?.takingInstructions === 'empty_stomach'
                                ? 'Na czczo'
                                : null;

                        return (
                          <div
                            key={entry.id}
                            onClick={() => toggleMedication(entry.id, isTaken)}
                            className={`rounded-3xl shadow-sm p-5 cursor-pointer transition-all duration-200 hover:shadow-md ${
                              isTaken
                                ? 'bg-[#F0FDF4] border border-[#BBF7D0]'
                                : 'bg-[#FAFAFA] border border-[#F0F0F0]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* Checkbox */}
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                                  isTaken
                                    ? 'bg-[#00A86B] border-[#00A86B]'
                                    : 'bg-white border-[#CCCCCC]'
                                }`}
                              >
                                {isTaken && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={3}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </div>

                              {/* Informacje o leku */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4
                                    className={`text-base font-medium ${
                                      isTaken
                                        ? 'text-gray-500 line-through'
                                        : 'text-gray-900'
                                    }`}
                                  >
                                    {medicationName}
                                  </h4>
                                  {isTaken && (
                                    <span className="px-2 py-0.5 bg-[#00A86B] text-white text-xs font-medium rounded-full">
                                      Przyjęty
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {entry.dosage} {entry.dosage === 1 ? 'sztuka' : 'sztuki'}
                                  {takingLabel && (
                                    <span className="ml-2 text-gray-500">• {takingLabel}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};